import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { WorklistViewModel } from 'models';

import { apiClient } from 'core/api/globals';

import { WorklistManagementPage as WorklistManagementPageComponent } from '../fragments/WorklistManagementPage';

export const WorklistManagementPage: FunctionComponent = () => {
  const [worklists, setWorklists] = useState<WorklistViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchWorklists = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch worklists from the API - get all worklists for admin view
      // Parameters: forAdmin, sessionLocationId, entity
      const fetchedWorklists = await apiClient.workListViewClient.getViews(true, 0, '');
      setWorklists(fetchedWorklists);
    } catch (error) {
      console.error('Error fetching worklists:', error);
      // Set sample data for demo purposes if API fails
      const sampleWorklists: WorklistViewModel[] = [
        {
          id: 1,
          user: 'admin@compumed.com',
          name: 'STAT Active Requests',
          ordinal: 1,
          parentViewId: null,
          columnState: null,
          dataState: JSON.stringify({
            filter: {
              logic: 'and',
              filters: [
                { field: 'ExamType', operator: 'eq', value: 'EKG' },
                { field: 'Status', operator: 'neq', value: 'Complete' },
              ],
            },
          }),
          oDataResourceString: null,
          entity: 'Exam',
        },
        {
          id: 2,
          user: 'user@compumed.com',
          name: 'REG Active Requests',
          ordinal: 2,
          parentViewId: null,
          columnState: null,
          dataState: JSON.stringify({
            filter: {
              logic: 'and',
              filters: [
                { field: 'Status', operator: 'neq', value: 'Cancelled' },
                { field: 'Status', operator: 'neq', value: 'Duplicate' },
              ],
            },
          }),
          oDataResourceString: null,
          entity: 'Patient',
        },
      ];
      setWorklists(sampleWorklists);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorklists();
  }, [fetchWorklists]);

  const handleUpdateWorklist = useCallback(async (updatedWorklist: WorklistViewModel) => {
    try {
      // Update worklist via API
      await apiClient.workListViewClient.updateView(updatedWorklist);

      // Update local state
      setWorklists((prev) => prev.map((worklist) => (worklist.id === updatedWorklist.id ? updatedWorklist : worklist)));
      console.log('Worklist updated:', updatedWorklist);
    } catch (error) {
      console.error('Error updating worklist:', error);
      // For demo purposes, still update locally even if API fails
      setWorklists((prev) => prev.map((worklist) => (worklist.id === updatedWorklist.id ? updatedWorklist : worklist)));
    }
  }, []);

  const handleDeleteWorklist = useCallback(async (worklistToDelete: WorklistViewModel) => {
    try {
      // Delete worklist via API (assuming this method exists)
      // await apiClient.worklistViewClient.deleteView(worklistToDelete.id);

      // Update local state
      setWorklists((prev) => prev.filter((worklist) => worklist.id !== worklistToDelete.id));
      console.log('Worklist deleted:', worklistToDelete);
    } catch (error) {
      console.error('Error deleting worklist:', error);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    fetchWorklists();
  }, [fetchWorklists]);

  return (
    <WorklistManagementPageComponent
      worklists={worklists}
      isLoading={isLoading}
      onUpdateWorklist={handleUpdateWorklist}
      onDeleteWorklist={handleDeleteWorklist}
      onRefresh={handleRefresh}
    />
  );
};
